import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  InputGroup,
  InputRightElement,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import FAQResource from 'api/faq';
import {
  activeStyles,
  normalStyles,
  separatorStyles,
} from 'assets/css/commonStyles';
import {
  Container,
  Next,
  PageGroup,
  Paginator,
  Previous,
  usePaginator,
} from 'chakra-paginator';
import PageLimitChanger from 'components/common/PageLimitChanger';
import ResultCountDisplay from 'components/common/ResultCountDisplay';
import TableSkeletonLoader from 'components/common/TableSkeletonLoader';
import FAQListItem from 'components/faq/FAQListItem';
import { strings } from 'config/localization';
import CreatedAtInput, {
  DEFAULT_PAGE_SIZE,
  INITIAL_CURRENT_PAGE,
  REACT_DATE_PICKER_FORMAT_YYYY_MM_DD,
} from 'constants/common';
import routes from 'constants/routes';
import React, { useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useForm } from 'react-hook-form';
import { BiCalendar, BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import { DateFormatMDY, getStartingSerialNumber } from 'utils';

interface FilterParams {
  currentPage: number;
  date: string;
  pageSize: number;
  search: string;
}

const FAQList: React.FC = () => {
  const [isLoading, setIsLoading] = useState<string | null>();
  const faqAPI = new FAQResource();

  const [totalPPE, setTotalPPE] = useState<number | undefined>(undefined);
  const [createdAt, setCreatedAt] = useState<any>();

  const [filterParams, setFilterParams] = useState<FilterParams>({
    currentPage: INITIAL_CURRENT_PAGE,
    date: '',
    pageSize: DEFAULT_PAGE_SIZE,
    search: '',
  });

  const { pagesQuantity, setPageSize } = usePaginator({
    total: totalPPE,
    initialState: {
      pageSize: DEFAULT_PAGE_SIZE,
      currentPage: INITIAL_CURRENT_PAGE,
    },
  });

  const { data: faqList, isLoading: isFAQListLoading } = useQuery(
    [
      'faqList',
      {
        page: filterParams.currentPage,
        date: filterParams.date,
        limit: filterParams.pageSize,
        search: filterParams.search,
      },
    ],
    async () => {
      const queryParams: any = {
        page: filterParams.currentPage,
        date: filterParams.date,
        limit: filterParams.pageSize,
        search: filterParams.search,
      };
      // if (filterParams.createdAt)
      //   queryParams.date = DateFormatMDY(filterParams.createdAt);
      const response = await faqAPI.list(queryParams);
      setTotalPPE(response?.data?.data?.meta?.total);
      response && setIsLoading(null);
      return response?.data?.data;
    }
  );

  const methods = useForm<any>();

  const filterList = async (data: any) => {
    setFilterParams((prevState) => ({
      ...prevState,
      currentPage: INITIAL_CURRENT_PAGE,
      date: DateFormatMDY(createdAt),
      search: data.search,
    }));
  };

  const handlePageChange = (nextPage: number) => {
    setFilterParams((prevState) => ({
      ...prevState,
      currentPage: nextPage,
    }));
  };

  /**
   * Handle reset filter params
   */
  const handleReset = () => {
    methods.reset();
    setCreatedAt('');
    setFilterParams((prevState) => ({
      ...prevState,
      currentPage: INITIAL_CURRENT_PAGE,
      date: '',
      search: '',
    }));
    setIsLoading(() => '');
  };

  const startingSN = useMemo(() => {
    return getStartingSerialNumber(
      filterParams.currentPage,
      filterParams.pageSize
    );
  }, [filterParams.currentPage, filterParams.pageSize]);

  const handleDateChange = (date: Date) => {
    setCreatedAt(date);
    methods.setValue('createdAt', createdAt);
  };

  /**
   * Handle filter params submit
   */
  const onSubmit = (data: any) => {
    filterList(data);
  };

  return (
    <>
      <Stack
        direction="column"
        spacing="4"
        bg="white"
        p={['3', '6']}
        shadow="box"
        w="full"
      >
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack
            direction={['column', 'row']}
            spacing="4"
            align={['start', 'end']}
          >
            <FormControl>
              <FormLabel>{strings.date}</FormLabel>
              <InputGroup>
                <InputRightElement
                  children={<BiCalendar color="green.500" />}
                />
                <ReactDatePicker
                  dateFormat={REACT_DATE_PICKER_FORMAT_YYYY_MM_DD}
                  customInput={<CreatedAtInput value={createdAt} />}
                  onChange={handleDateChange}
                  selected={createdAt}
                  value={DateFormatMDY(createdAt)}
                  shouldCloseOnSelect={false}
                  autoComplete="off"
                />
              </InputGroup>
            </FormControl>
            <Stack direction="row" spacing="4" pt={['2', '8']}>
              <Button colorScheme="primary" type="submit">
                {strings.filter}
              </Button>
              <Button
                colorScheme="primary"
                type="submit"
                variant="outline"
                onClick={handleReset}
              >
                {strings.reset_filter}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Stack>
      <Stack direction="column" spacing="4">
        <Breadcrumb color="gray.400" size="4">
          <BreadcrumbItem>
            <BreadcrumbLink>{strings.faq}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage color="gray.900">
            <BreadcrumbLink as={RouterLink} to={routes.faqs.list}>
              {strings.all_faq}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <Flex justify="space-between">
          <Heading size="md">{strings.faq_list}</Heading>
        </Flex>
        <Stack bg="white" p={['3', '6']} shadow="box" rounded="sm">
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>{strings.description}</Th>
                  <Th>{strings.date}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {!isFAQListLoading &&
                  faqList?.results?.map((faqData: any, index: number) => (
                    <FAQListItem
                      faqData={faqData}
                      key={faqData.id}
                      index={startingSN + index}
                    />
                  ))}
                {isFAQListLoading && (
                  <TableSkeletonLoader rows={filterParams.pageSize} cols={2} />
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Stack>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          overflow="auto"
        >
          <Text color="gray.400">
            <ResultCountDisplay
              isLoading={isFAQListLoading}
              totalCount={totalPPE}
              resultCount={faqList?.results?.length}
              pageSize={filterParams.pageSize}
              currentPage={filterParams.currentPage}
            />
          </Text>
          <Flex alignItems="center" flexWrap={['wrap', 'nowrap']}>
            {faqList?.results?.length > 0 && (
              <PageLimitChanger
                pageSize={filterParams.pageSize}
                setPageSize={(value: any) => {
                  setFilterParams((prevState) => ({
                    ...prevState,
                    currentPage: INITIAL_CURRENT_PAGE,
                    pageSize: value,
                  }));
                  setPageSize(value);
                }}
              />
            )}
            {!isFAQListLoading && pagesQuantity > 1 && (
              <Paginator
                activeStyles={activeStyles}
                currentPage={filterParams.currentPage}
                normalStyles={normalStyles}
                separatorStyles={separatorStyles}
                pagesQuantity={pagesQuantity}
                onPageChange={handlePageChange}
                innerLimit={2}
                outerLimit={2}
              >
                <Container
                  align="center"
                  justifyContent="flex-end"
                  w="full"
                  p={1}
                  pr={0}
                  ml={3}
                >
                  <Previous mr="0.3rem" sx={normalStyles} minWidth={0}>
                    <Icon as={BiChevronLeft} fontSize="xl" />
                  </Previous>
                  <PageGroup isInline align="center" />
                  <Next ml="0.3rem" sx={normalStyles} minWidth={0}>
                    <Icon as={BiChevronRight} fontSize="xl" />
                  </Next>
                </Container>
              </Paginator>
            )}
          </Flex>
        </Flex>
      </Stack>
    </>
  );
};

export default FAQList;
